import { useState } from 'react';
import { Check, ChevronDown, ChevronRight, Circle, Loader2 } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

export interface Todo {
  subject?: string;
  content?: string;
  description?: string;
  activeForm?: string;
  status?: 'pending' | 'in_progress' | 'completed' | string;
}

/**
 * Sticky panel showing the agent's current TodoWrite state. Counts
 * completed/total in the collapsed header; expanded view shows every item
 * with its status icon and active form when running. Empty if the agent
 * hasn't called TodoWrite yet.
 */
export function TodosPanel({ todos, className }: { todos: Todo[]; className?: string }) {
  const [open, setOpen] = useState(false);
  if (todos.length === 0) return null;
  const total = todos.length;
  const done = todos.filter((t) => t.status === 'completed').length;
  const inProgress = todos.find((t) => t.status === 'in_progress');

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className={cn(
        'rounded-md border bg-background/95 shadow-sm',
        className,
      )}
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs"
        >
          {open ? (
            <ChevronDown className="size-3 shrink-0" />
          ) : (
            <ChevronRight className="size-3 shrink-0" />
          )}
          <span className="font-semibold">Todos</span>
          <span className="text-muted-foreground">
            {done}/{total}
          </span>
          {inProgress && !open && (
            <span className="min-w-0 flex-1 truncate italic text-muted-foreground">
              {inProgress.activeForm ?? inProgress.subject ?? inProgress.content}
            </span>
          )}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t px-3 py-2">
        <ul className="space-y-1 text-sm">
          {todos.map((t, i) => (
            <li key={i} className="flex items-start gap-2">
              <TodoIcon status={t.status} />
              <span
                className={cn(
                  'min-w-0 flex-1',
                  t.status === 'completed' && 'text-muted-foreground line-through',
                )}
              >
                {t.status === 'in_progress' && t.activeForm
                  ? t.activeForm
                  : t.subject ?? t.content ?? '(unnamed)'}
                {t.description && (
                  <span className="block text-[11px] text-muted-foreground">{t.description}</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}

function TodoIcon({ status }: { status?: string | undefined }) {
  if (status === 'completed') return <Check className="mt-0.5 size-3.5 shrink-0 text-emerald-500" />;
  if (status === 'in_progress')
    return <Loader2 className="mt-0.5 size-3.5 shrink-0 animate-spin text-primary" />;
  return <Circle className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />;
}
